import { useEffect, useRef, useState, useCallback } from 'react'
import type { PortfolioDeltaMessage } from '../types/delta'
import { refreshAccessToken } from '../api/auth'
import { isTokenExpired } from '../lib/jwt'
import { notifySessionExpired } from '../auth/sessionExpired'

type StreamStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

/**
 * Subscribes to the backend WebSocket at /ws/portfolio.
 *
 * Protocol: on open, sends a JSON handshake as the first text frame:
 * {"token":"<jwt>","portfolioId":"<wallet_address>"}
 * The server validates the JWT inside the handler (no token in the URL).
 * Subsequent frames are JSON-serialised PortfolioDeltaMessage objects.
 *
 * Reconnects automatically with exponential back-off (1s → 2s → 4s … max 30s).
 * If the JWT is expired it attempts a silent refresh before reconnecting.
 */
export function usePortfolioStream(
  portfolioId: string | null,
  token?: string | null,
  onTokensRefreshed?: (accessToken: string, refreshToken: string) => void,
) {
  const [latest, setLatest] = useState<PortfolioDeltaMessage | null>(null)
  const [status, setStatus] = useState<StreamStatus>('disconnected')
  const wsRef = useRef<WebSocket | null>(null)
  const retryDelay = useRef(1000)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const disconnect = useCallback(() => {
    if (retryTimer.current) clearTimeout(retryTimer.current)
    wsRef.current?.close()
    wsRef.current = null
    setStatus('disconnected')
  }, [])

  const connect = useCallback(() => {
    if (!portfolioId) return
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    const jwt = token ?? sessionStorage.getItem('jwt')
    if (jwt && isTokenExpired(jwt)) {
      const storedRefresh = sessionStorage.getItem('refreshJwt')
      if (storedRefresh) {
        refreshAccessToken(storedRefresh)
          .then(data => {
            if (data.refreshToken) {
              onTokensRefreshed?.(data.accessToken, data.refreshToken)
            }
            // connect() will be re-invoked via the token dep change in useEffect
          })
          .catch(err => {
            setStatus('error')
            // A definitive 401 from /api/auth/refresh means the refresh
            // token itself is invalid/expired/already-rotated (reuse
            // detected) — the session is genuinely over, not a transient
            // network blip. Only that specific case should force the user
            // back to the login screen.
            if (String((err as Error)?.message ?? '').startsWith('401')) {
              notifySessionExpired()
              return
            }
            // Anything else (network blip, 5xx, timeout) — retry with the
            // same backoff ws.onclose uses below, instead of leaving the
            // hook stuck in 'error' with nothing scheduled to recover it.
            retryTimer.current = setTimeout(() => {
              retryDelay.current = Math.min(retryDelay.current * 2, 30_000)
              connect()
            }, retryDelay.current)
          })
        return
      }
      // Access token expired and there's no refresh token to try — nothing
      // left that could restore the session.
      setStatus('error')
      notifySessionExpired()
      return
    }

    setStatus('connecting')
    const base = import.meta.env.VITE_WS_URL
      || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/portfolio`
    const ws = new WebSocket(base)
    wsRef.current = ws

    ws.onopen = () => {
      retryDelay.current = 1000
      // First frame: JSON handshake — JWT stays off the URL to avoid log exposure
      ws.send(JSON.stringify({ token: jwt ?? '', portfolioId }))
      setStatus('connected')
    }

    ws.onmessage = (evt: MessageEvent<string>) => {
      try {
        const data = JSON.parse(evt.data) as PortfolioDeltaMessage
        setLatest(data)
      } catch {
        // ignore malformed frames
      }
    }

    ws.onclose = () => {
      // If disconnect() was called it nulls wsRef before onclose fires — don't retry.
      if (wsRef.current !== ws) return
      setStatus('disconnected')
      setLatest(null) // reset so the poll fallback can take over while WS is down
      if (portfolioId) {
        // Reconnect with back-off
        retryTimer.current = setTimeout(() => {
          retryDelay.current = Math.min(retryDelay.current * 2, 30_000)
          connect()
        }, retryDelay.current)
      }
    }

    ws.onerror = () => {
      setStatus('error')
      ws.close()
    }
  }, [portfolioId, token, onTokensRefreshed])

  useEffect(() => {
    if (portfolioId) {
      connect()
    } else {
      disconnect()
    }
    return disconnect
  }, [portfolioId, token, connect, disconnect])

  return { latest, status }
}
